import { c as _c } from "react/compiler-runtime";
import { ReactNode, useContext } from 'react';
import useSWR from 'swr';
import { APIBatchOperation } from '../../../../../../typings/BatchOperation.interface';
import { EventStatus as IEventStatus } from '../../../../../../typings/Event.interface';
import APIMethodKeys from '../../../client/APIMethodKeys';
import { GlobalContext } from '../../contexts/GlobalContext';
import { StyledCard, StyledCardSection } from '../base/Card';
import Chip from '../base/Chip';
import ChipOverflow from '../base/ChipOverflow';
import Icon, { IconName } from '../base/Icon';
import Text from '../base/Text';
import { event_list_filters, request_list_filters, search_filter_component } from '../Filters/FilterComponents';
import { Div } from '../helpers/StyledUtils';
import { Status } from '../Status';
import { BulkOperationFilters, BulkOperationModel, BulkOperationType } from './types';
import useBulkRetryRenderData, { BulkRetryRenderData } from './useBulkRetryRenderData';
import { prettyPrintDate } from './utils';
import { capitalizeFirstLetter } from '../../../utils';
import { useDatePreferences } from '../../hooks/useDatePreferences';
type FilterBreakdownBaseConfig = {
  render: (values: any, render_data: BulkRetryRenderData, timezone: string) => string | ReactNode | ReactNode[];
};
type FilterBreakdownConfigRenderLabel = {
  renderIcon: (values: any) => ReactNode;
  renderLabel: (values: any) => ReactNode;
  label?: never;
  label_plural?: never;
};
type FilterBreakdownConfigStaticLabel = {
  icon: IconName;
  label: string;
  label_plural?: string;
  renderLabel?: never;
};
const hasRenderLabel = (config: FilterBreakdownBaseConfig & (FilterBreakdownConfigRenderLabel | FilterBreakdownConfigStaticLabel)): config is FilterBreakdownBaseConfig & FilterBreakdownConfigRenderLabel => {
  return 'renderLabel' in config;
};
type FilterBreakdownConfig = FilterBreakdownBaseConfig & (FilterBreakdownConfigRenderLabel | FilterBreakdownConfigStaticLabel);

// The API accepts id filters (webhook_id, source_id, destination_id, cli_user_id) as either a
// single string or an array, and stores the query verbatim. Operations created through the API
// with a single string value would otherwise crash the `.map` below.
const toArray = <T,>(value: T | T[] | null | undefined): T[] => {
  if (value === null || value === undefined) return [];
  return Array.isArray(value) ? value : [value];
};

// Helper function to get filter icons based on model
const getModelFilterIcons = (model?: BulkOperationModel): Record<string, IconName> => {
  if (!model || model === 'events') {
    return event_list_filters.reduce((obj, filter) => {
      obj[filter.filter_key] = filter.icon;
      return obj;
    }, {} as Record<string, IconName>);
  }
  return request_list_filters.reduce((obj, filter) => {
    obj[filter.filter_key] = filter.icon;
    return obj;
  }, {} as Record<string, IconName>);
};

// Helper hook to get bulk operation details
const useBulkOperation = id => {
  const $ = _c(5);
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  let t0;
  if ($[0] !== id) {
    t0 = APIMethodKeys.bulk.events.get(id);
    $[0] = id;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== HookdeckAPI.bulk.events || $[3] !== id) {
    t1 = () => HookdeckAPI.bulk.events.get(id);
    $[2] = HookdeckAPI.bulk.events;
    $[3] = id;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const {
    data: bulk_operation
  } = useSWR(t0, t1);
  return bulk_operation;
};

// Components for rendering bulk operation references
const BulkOperationIcon: React.FC<{
  id: string;
}> = t0 => {
  const $ = _c(2);
  const {
    id
  } = t0;
  const bulk_operation = useBulkOperation(id);
  const type = bulk_operation ? bulk_operation?.type : "events.retry";
  const icon_name = type === "events.retry" ? "retry" : "cancel";
  let t1;
  if ($[0] !== icon_name) {
    t1 = <Icon muted={true} icon={icon_name} left={true} />;
    $[0] = icon_name;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
const BulkOperationLabel: React.FC<{
  id: string;
}> = t0 => {
  const $ = _c(3);
  const {
    id
  } = t0;
  const bulk_operation = useBulkOperation(id);
  if (!bulk_operation) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <>Bulk operation</>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  const t1 = bulk_operation?.type === "events.retry" ? "Bulk retry" : "Bulk cancel";
  let t2;
  if ($[1] !== t1) {
    t2 = <>{t1}</>;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
};
const BulkOperationRender: React.FC<{
  id: string;
}> = t0 => {
  const $ = _c(18);
  const {
    id
  } = t0;
  const {
    timezone
  } = useDatePreferences();
  const bulk_operation = useBulkOperation(id);
  if (!bulk_operation) {
    let t1;
    if ($[0] !== id) {
      t1 = <>{id}</>;
      $[0] = id;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  let t1;
  if ($[2] !== bulk_operation.created_at || $[3] !== timezone) {
    t1 = prettyPrintDate(bulk_operation.created_at, timezone);
    $[2] = bulk_operation.created_at;
    $[3] = timezone;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const date = t1;
  let t2;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      align: "center",
      gap: 1
    };
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== bulk_operation.number) {
    t3 = <Text size="s">#{bulk_operation.number}</Text>;
    $[6] = bulk_operation.number;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <Text size="s">-</Text>;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== date) {
    t5 = <Text size="s">{date}</Text>;
    $[9] = date;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Text size="s">-</Text>;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== bulk_operation.id) {
    t7 = <Text size="s" muted={true}>{bulk_operation.id}</Text>;
    $[12] = bulk_operation.id;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t3 || $[15] !== t5 || $[16] !== t7) {
    t8 = <Div flex={t2}>{t3}{t4}{t5}{t6}{t7}</Div>;
    $[14] = t3;
    $[15] = t5;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  return t8;
};

// Common filter configurations
const common: Record<string, FilterBreakdownConfig> = {
  search_term: {
    label: 'Search term',
    label_plural: 'Search terms',
    icon: search_filter_component.icon,
    render: v => v
  },
  created_at: {
    label: 'Created Date',
    icon: 'date',
    render: ({
      gte,
      lte
    }, _render_data, timezone) => {
      if (gte === lte) {
        return prettyPrintDate(gte, timezone);
      }
      if (gte && lte) {
        return `between ${prettyPrintDate(gte, timezone)} and ${prettyPrintDate(lte, timezone)}`;
      }
      return `${lte ? 'lesser or equal to' : 'greater or equal to'} ${prettyPrintDate(gte || lte, timezone)}`;
    }
  },
  body: {
    label: 'Request Body',
    icon: 'code',
    render: () => {
      return `filters matches the active body request filters`;
    }
  },
  headers: {
    label: 'Request Headers',
    icon: 'code',
    render: () => {
      return `filters matches the active headers request filters`;
    }
  },
  path: {
    label: 'Request Path',
    icon: 'code',
    render: () => {
      return `filters matches the active path request filters`;
    }
  },
  query: {
    label: 'Request Query',
    icon: 'code',
    render: () => {
      return `filters matches the active query request filters`;
    }
  },
  source_id: {
    label: 'Source',
    label_plural: 'Sources',
    icon: 'source',
    render: (values, {
      sources
    }) => toArray(values).map((v: string) => {
      const source = sources?.find(source => source.id === v);
      return source ? <Chip key={v} outline mono title={source.name} label={source.name} to={`/sources/${source.id}`} /> : 'Failed to fetch name';
    })
  },
  bulk_retry_id: {
    renderIcon: value => <BulkOperationIcon id={value} />,
    renderLabel: value => <BulkOperationLabel id={value} />,
    render: value => <BulkOperationRender id={value} />
  }
};

// Model-specific filter configurations
const getModelRenderConfigs = (operation_type: BulkOperationType, model?: BulkOperationModel): Record<string, FilterBreakdownConfig> => {
  const filterIcons = getModelFilterIcons(model);
  if (operation_type === 'cancel') {
    // Cancel operations only work with events
    return {
      ...common,
      status: {
        label: 'Status',
        icon: filterIcons.status,
        render: key => Array.isArray(key) ? <Div flex={{
          gap: 2,
          align: 'center',
          wrap: true
        }}>
              {key.map((k: string) => <Status key={k} status={k as IEventStatus} response_status={null} />)}
            </Div> : <Status status={key as IEventStatus} response_status={null} />
      },
      response_status: {
        label: 'HTTP Status',
        icon: 'http',
        render: value => {
          if (Array.isArray(value)) {
            return value.join(', ');
          }
          if (value.lte === value.gte) {
            return value.lte;
          }
          if (value.lte && value.gte) {
            return `between ${value.gte} and ${value.lte}`;
          }
          return `${value.lte ? 'lesser or equal to' : 'greater or equal to'} ${value.lte || value.gte}`;
        }
      },
      destination_id: {
        label: 'Destination',
        label_plural: 'Destinations',
        icon: filterIcons.destination_id,
        render: (values, {
          destinations
        }) => toArray(values).map((v: string) => {
          const destination = destinations?.find(destination => destination.id === v);
          return destination ? <Chip key={v} outline mono title={destination.name} label={destination.name} to={`/destinations/${destination.id}`} /> : 'Failed to fetch name';
        })
      },
      cli_user_id: {
        label: 'CLI User',
        label_plural: 'CLI Users',
        icon: filterIcons.cli_user_id,
        render: (values, {
          team_members
        }) => toArray(values).map((v: string) => `${team_members?.find(member => member.user_id === v)?.user_email || 'Failed to fetch name'}`).join(', ')
      },
      issue_id: {
        label: 'Issue',
        label_plural: 'Issues',
        icon: 'issues',
        render: value => Array.isArray(value) ? value.map((v: string) => <Chip key={v} mono title={v} label={v} to={`/issues/${v}`} />) : value
      },
      error_code: {
        label: 'Error Code',
        label_plural: 'Error Codes',
        icon: 'error',
        render: key => Array.isArray(key) ? key.join(', ') : key
      },
      webhook_id: {
        label: 'Connection',
        label_plural: 'Connections',
        icon: filterIcons.webhook_id,
        render: (values, {
          webhooks
        }) => toArray(values).map((v: string) => {
          const webhook = webhooks?.find(webhook => webhook.id === v);
          return webhook ? <Chip key={v} outline mono title={webhook.full_name} label={webhook.full_name} to={`/connections/${webhook.id}`} /> : 'Failed to fetch name';
        })
      },
      next_attempt_at: {
        label: 'Scheduled Attempts',
        icon: filterIcons.scheduled_attempts,
        render: value => value ? 'Has scheduled attempts' : 'No scheduled attempts'
      }
    };
  }

  // Retry operations with model-specific configs
  if (!model) {
    return common;
  }
  const base_configs: Record<string, FilterBreakdownConfig> = {
    ...common,
    status: {
      label: model === 'events' ? 'Status' : 'Request Status',
      icon: filterIcons.status,
      render: key => Array.isArray(key) ? <Div flex={{
        gap: 2,
        align: 'center',
        wrap: true
      }}>
            {key.map((k: string) => <Status key={k} status={k as IEventStatus} response_status={null} />)}
          </Div> : <Status status={key as IEventStatus} response_status={null} />
    },
    response_status: {
      label: 'HTTP Status',
      icon: 'http',
      render: value => {
        if (Array.isArray(value)) {
          return value.join(', ');
        }
        if (value.lte === value.gte) {
          return value.lte;
        }
        if (value.lte && value.gte) {
          return `between ${value.gte} and ${value.lte}`;
        }
        return `${value.lte ? 'lesser or equal to' : 'greater or equal to'} ${value.lte || value.gte}`;
      }
    },
    destination_id: {
      label: 'Destination',
      label_plural: 'Destinations',
      icon: filterIcons.destination_id,
      render: (values, {
        destinations
      }) => toArray(values).map((v: string) => {
        const destination = destinations?.find(destination => destination.id === v);
        return destination ? <Chip outline mono title={destination.name} label={destination.name} to={`/destinations/${destination.id}`} /> : 'Failed to fetch name';
      })
    },
    cli_user_id: {
      label: 'CLI User',
      label_plural: 'CLI Users',
      icon: filterIcons.cli_user_id,
      render: (values, {
        team_members
      }) => toArray(values).map((v: string) => `${team_members?.find(member => member.user_id === v)?.user_email || 'Failed to fetch name'}`).join(', ')
    },
    issue_id: {
      label: 'Issue',
      label_plural: 'Issues',
      icon: 'issues',
      render: value => Array.isArray(value) ? value.map((v: string) => <Chip key={v} mono title={v} label={v} to={`/issues/${v}`} />) : value
    },
    error_code: {
      label: 'Error Code',
      label_plural: 'Error Codes',
      icon: 'error',
      render: key => Array.isArray(key) ? key.join(', ') : key
    },
    webhook_id: {
      label: 'Connection',
      label_plural: 'Connections',
      icon: filterIcons.webhook_id,
      render: (values, {
        webhooks
      }) => toArray(values).map((v: string) => {
        const webhook = webhooks?.find(webhook => webhook.id === v);
        return webhook ? <Chip outline mono title={webhook.full_name} label={webhook.full_name} to={`/connections/${webhook.id}`} /> : 'Failed to fetch name';
      })
    },
    next_attempt_at: {
      label: 'Scheduled Attempts',
      icon: filterIcons.scheduled_attempts,
      render: value => value ? 'Has scheduled attempts' : 'No scheduled attempts'
    }
  };

  // Add model-specific configurations
  if (model === 'requests') {
    base_configs.rejection_cause = {
      label: 'Rejection Cause',
      label_plural: 'Rejection Causes',
      icon: filterIcons.rejection_cause,
      render: key => Array.isArray(key) ? key.join(', ') : key
    };
    base_configs.ingested_at = {
      label: 'Ingested Date',
      icon: 'date',
      render: ({
        gte,
        lte
      }, _render_data, timezone) => {
        if (gte === lte) {
          return prettyPrintDate(gte, timezone);
        }
        if (gte && lte) {
          return `between ${prettyPrintDate(gte, timezone)} and ${prettyPrintDate(lte, timezone)}`;
        }
        return `${lte ? 'lesser or equal to' : 'greater or equal to'} ${prettyPrintDate(gte || lte, timezone)}`;
      }
    };
    base_configs.status = {
      label: 'Request Status',
      icon: filterIcons.status,
      render: key => capitalizeFirstLetter(key.toLowerCase())
    };
  }
  if (model === 'ignored_events') {
    base_configs.cause = {
      label: 'Cause',
      label_plural: 'Causes',
      icon: filterIcons.status,
      render: key => Array.isArray(key) ? key.join(', ') : key
    };
    base_configs.transformation_id = {
      label: 'Transformation',
      label_plural: 'Transformations',
      icon: 'transformation',
      render: key => Array.isArray(key) ? key.join(', ') : key
    };
    base_configs.webhook_id = {
      label: 'Connection',
      label_plural: 'Connections',
      icon: filterIcons.webhook_id,
      render: (values, {
        webhooks
      }) => toArray(values).map((v: string) => {
        const webhook = webhooks?.find(webhook => webhook.id === v);
        return webhook ? webhook.full_name : 'Failed to fetch name';
      }).join(', ')
    };
  }
  return base_configs;
};

// Titled card shell for breakdown lists: a small muted header section on top of a
// section holding the rows. Background stays transparent so it inherits the muted
// expanded-row surface it usually sits on.
export const BulkOperationBreakdownCard: React.FC<{
  title: string;
  children: ReactNode;
}> = t0 => {
  const $ = _c(10);
  const {
    title,
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      backgroundColor: "transparent"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      x: 3,
      y: 1
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== title) {
    t3 = <StyledCardSection p={t2}><Text size="xs" semi={true} muted={true}>{title}</Text></StyledCardSection>;
    $[2] = title;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      direction: "column",
      gap: 3
    };
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== children) {
    t5 = <StyledCardSection p={3} flex={t4}>{children}</StyledCardSection>;
    $[5] = children;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t3 || $[8] !== t5) {
    t6 = <StyledCard overflow_hidden={true} style={t1}>{t3}{t5}</StyledCard>;
    $[7] = t3;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  return t6;
};
export const BulkOperationBreakdownRow: React.FC<{
  icon: ReactNode;
  label: ReactNode;
  children: ReactNode;
}> = t0 => {
  const $ = _c(12);
  const {
    icon,
    label,
    children
  } = t0;
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      px: 20
    };
    t2 = {
      align: "center"
    };
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  let t3;
  if ($[2] !== label) {
    t3 = <Text muted={true} size="s" text_wrap={false}>{label}</Text>;
    $[2] = label;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== icon || $[5] !== t3) {
    t4 = <Div w={30} h={t1} flex={t2}>{icon}{t3}</Div>;
    $[4] = icon;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== children) {
    t5 = <Div w={60}><Text as="p" size="s" m={0}>{children}</Text></Div>;
    $[7] = children;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t4 || $[10] !== t5) {
    t6 = <Div flex={true}>{t4}{t5}</Div>;
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  return t6;
};
interface BulkOperationBreakdownProps {
  operation_type: BulkOperationType;
  model?: BulkOperationModel;
  query: BulkOperationFilters;
  /** When set, wraps the rows in a BulkOperationBreakdownCard with this header title. */
  title?: string;
}
const BulkOperationBreakdown: React.FC<BulkOperationBreakdownProps> = t0 => {
  const $ = _c(16);
  const {
    operation_type,
    model,
    query,
    title
  } = t0;
  const {
    timezone
  } = useDatePreferences();
  const {
    data,
    loading
  } = useBulkRetryRenderData(query);
  let t1;
  let t2;
  if ($[0] !== data || $[1] !== loading || $[2] !== model || $[3] !== operation_type || $[4] !== query || $[5] !== timezone) {
    t2 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const render_configs = getModelRenderConfigs(operation_type, model);
      if (loading) {
        let t3;
        if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
          t3 = <div><Icon icon="loading" muted={true} /></div>;
          $[8] = t3;
        } else {
          t3 = $[8];
        }
        t2 = t3;
        break bb0;
      }
      const keys = Object.keys(query).filter(key => {
        if (!render_configs[key]) {
          return false;
        }
        const value = (query as any)[key];
        if (Array.isArray(value) && value.length === 0 || value === null || value === undefined) {
          return false;
        }
        if (typeof value === "object" && !Array.isArray(value)) {
          return Object.values(value).some(_temp);
        }
        return !!value;
      });
      if (keys.length === 0) {
        t2 = null;
        break bb0;
      }
      t1 = keys.map(key_0 => {
        const render_config = render_configs[key_0];
        const render = render_config.render((query as any)[key_0], data, timezone);
        const multiple = Array.isArray(render) && render.length > 1;
        const label = hasRenderLabel(render_config) ? render_config.renderLabel((query as any)[key_0]) : multiple ? render_config.label_plural || render_config.label : render_config.label;
        const render_icon = hasRenderLabel(render_config) && render_config.renderIcon((query as any)[key_0]);
        return <BulkOperationBreakdownRow key={key_0} icon={render_icon || <Icon muted={true} icon={(render_config as FilterBreakdownConfigStaticLabel).icon} left={true} />} label={label}>{Array.isArray(render) && render.length > 1 ? <ChipOverflow chips={render} /> : render}</BulkOperationBreakdownRow>;
      });
    }
    $[0] = data;
    $[1] = loading;
    $[2] = model;
    $[3] = operation_type;
    $[4] = query;
    $[5] = timezone;
    $[6] = t1;
    $[7] = t2;
  } else {
    t1 = $[6];
    t2 = $[7];
  }
  if (t2 !== Symbol.for("react.early_return_sentinel")) {
    return t2;
  }
  const rows = t1;
  if (title) {
    let t3;
    if ($[9] !== rows || $[10] !== title) {
      t3 = <BulkOperationBreakdownCard title={title}>{rows}</BulkOperationBreakdownCard>;
      $[9] = rows;
      $[10] = title;
      $[11] = t3;
    } else {
      t3 = $[11];
    }
    return t3;
  }
  let t3;
  let t4;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      direction: "column",
      gap: 3
    };
    t4 = {
      backgroundColor: "transparent"
    };
    $[12] = t3;
    $[13] = t4;
  } else {
    t3 = $[12];
    t4 = $[13];
  }
  let t5;
  if ($[14] !== rows) {
    t5 = <StyledCard p={3} flex={t3} style={t4}>{rows}</StyledCard>;
    $[14] = rows;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  return t5;
};
export default BulkOperationBreakdown;
function _temp(v) {
  return !!v;
}